import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAppDispatch, useAppSelector } from '../app/store';
import { logout as logoutAction, setCredentials } from '../features/auth/authSlice';

export function useAuth() {
  const dispatch = useAppDispatch();
  const { user, accessToken } = useAppSelector((s) => s.auth);

  // Step 1: password → server emails an OTP and returns a challenge token.
  const login = useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      const res = await api.post('/auth/login', creds);
      return res.data.data as { otpRequired: boolean; otpToken: string; email: string };
    },
  });

  // Step 2: submit the emailed code with the challenge token → session.
  const verifyOtp = useMutation({
    mutationFn: async (v: { otpToken: string; code: string }) => {
      const res = await api.post('/auth/verify-otp', v);
      return res.data.data as { accessToken: string; user: Parameters<typeof setCredentials>[0]['user'] };
    },
    onSuccess: (data) => dispatch(setCredentials(data)),
  });

  const resendOtp = useMutation({
    mutationFn: (otpToken: string) => api.post('/auth/resend-otp', { otpToken }),
  });

  const logout = async () => {
    await api.post('/auth/logout').catch(() => undefined);
    dispatch(logoutAction());
  };

  return {
    user,
    isAuthenticated: !!accessToken,
    isAdmin: user?.userType === 'ADMIN',
    // A client whose admin restricted them to read-only access — UI hides all trading actions.
    isViewOnly: user?.userType === 'CLIENT' && user?.accessLevel === 'VIEW_ONLY',
    login,
    verifyOtp,
    resendOtp,
    logout,
  };
}
